import { useMutation, UseMutationResult, useQueryClient } from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { BooleanRule, PushRulesResponse } from '../generated-types';
import { toggleBooleanNotificationRule } from '../services/pushNotificationRuleService';
import { PUSH_NOTIFICATION_RULES_QUERY_KEY } from './usePushNotificationRules';

const useToggleBooleanPush = (
    notificationRule: BooleanRule | undefined,
): UseMutationResult<
    boolean,
    RequestExceptionWithResponse,
    boolean,
    {
        previousState: PushRulesResponse | undefined;
    }
> => {
    const queryClient = useQueryClient();
    return useMutation<
        boolean,
        RequestExceptionWithResponse,
        boolean,
        {
            previousState: PushRulesResponse | undefined;
        }
    >({
        mutationFn: (enable) => toggleBooleanNotificationRule(notificationRule, enable),
        onMutate: async (newState) => {
            await queryClient.cancelQueries({ queryKey: [PUSH_NOTIFICATION_RULES_QUERY_KEY] });
            const previousState = queryClient.getQueryData<PushRulesResponse>([PUSH_NOTIFICATION_RULES_QUERY_KEY]);

            queryClient.setQueryData(
                [PUSH_NOTIFICATION_RULES_QUERY_KEY],
                (prevValues: PushRulesResponse | undefined = {} as PushRulesResponse) => {
                    return {
                        rules: prevValues?.rules?.map((rule) => {
                            if (rule.type === notificationRule?.type) {
                                return { ...rule, enabled: newState };
                            }
                            return rule;
                        }),
                    };
                },
            );

            return { previousState };
        },
        onError: (_error, _data, context) => {
            queryClient.setQueryData([PUSH_NOTIFICATION_RULES_QUERY_KEY], context?.previousState);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [PUSH_NOTIFICATION_RULES_QUERY_KEY] });
        },
    });
};

export default useToggleBooleanPush;
